import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import Button from '../common/Button';
import { useSignIn, useSignInWithProvider } from '../../core/account/AuthServiceHooks';
import Yup, { useYupValidationResolver } from '../../core/helpers/yup';
import FormInput from '../common/FormInput';
import FormPasswordInput from '../common/FormPasswordInput';
import {
  AuthenticationStep,
  CognitoAuthProvider,
  CustomAuthProvider,
  SocialProviderSequence,
} from '../../core/constants';

const AppleLoginConfirmationModal = dynamic(() => import('./AppleLoginConfirmationModal'));

type FormValues = {
  email: string;
  password: string;
};

type Props = {
  email?: string;
  showTitle?: boolean;
  showInModal?: boolean;
  hasSignupBtn?: boolean;
  setIsOpenSignUp?: () => void;
  confirmSignUpCallback?: (email: string) => void;
  redirect?: string;
};
const LoginCard = ({
  email,
  showTitle = true,
  hasSignupBtn = true,
  showInModal = false,
  setIsOpenSignUp,
  redirect,
  confirmSignUpCallback,
}: Props) => {
  const { t } = useTranslation(['common', 'component']);
  const router = useRouter();
  const redirectPath =
    (
      router.query as {
        redirect?: string;
      }
    ).redirect || redirect;
  const [showModal, setShowModal] = useState(false);
  const [signIn, loading, error] = useSignIn(redirectPath, confirmSignUpCallback);
  const [signInWithProvider] = useSignInWithProvider(AuthenticationStep.LOGIN, redirectPath);

  const schema = Yup.object({
    email: Yup.string().label(t('component:login-card.label.email')).required(),
    password: Yup.string().label(t('component:login-card.label.password')).required(),
  });

  const { handleSubmit, control, formState, getValues, setError } = useForm<FormValues>({
    defaultValues: {
      email: (router?.query?.email as string) || email || '',
      password: '',
    },
    mode: 'all',
    resolver: useYupValidationResolver(schema),
  });

  const events = {
    handleSubmit: async (data: FormValues) => {
      await signIn(data.email, data.password);
    },
    onClickPasswordReset: () => {
      const parameter = new URLSearchParams({
        email: getValues('email'),
        redirect: redirectPath || '',
      });
      router.push(`/account/password-reset?${parameter.toString()}`);
    },
    onClickProviderLogin: async (provider: CognitoAuthProvider | CustomAuthProvider) => {
      if (
        process.env.NEXT_PUBLIC_FEATURE_FLAG_SSO_APPLE_CONFIRMATION_MODAL === 'true' &&
        provider === CognitoAuthProvider.APPLE &&
        !showModal
      ) {
        setShowModal(true);
      } else {
        await signInWithProvider(provider);
      }
    },
  };

  useEffect(() => {
    if (error && !loading) {
      Object.keys(error).forEach((key) => {
        setError(key as 'email' | 'password', { types: { custom: error[key] } });
      });
    }
  }, [error, loading, setError]);

  return (
    <>
      <div className="relative flex flex-col items-center">
        {showTitle && (
          <div className="w-full text-left text-large font-bold">
            {t('component:login_card.title')}
          </div>
        )}
        <form className="w-full" onSubmit={handleSubmit(events.handleSubmit)}>
          <div className={classNames('flex flex-col w-full', { 'mt-8': showTitle })}>
            <FormInput
              background="gray"
              border={false}
              name="email"
              control={control}
              autoComplete="username"
              label={t('component:login_card.label.email')}
              labelClassName="font-bold"
              size="large"
              type="text"
              placeholder={t('component:login_card.placeholder.email')}
            />
          </div>

          <div className="relative mt-6 flex w-full flex-col">
            {/* password */}
            <FormPasswordInput
              label={t('component:login_card.label.password')}
              background="gray"
              border={false}
              labelClassName="font-bold"
              control={control}
              size="large"
              showError
              onKeyUp={(key) => {
                if (key === 'Enter') {
                  handleSubmit(events.handleSubmit);
                }
              }}
              placeholder={t('component:login_card.placeholder.password')}
            />
          </div>

          {/* login button */}
          <Button
            className="mt-6 w-full font-bold"
            type="submit"
            size="medium"
            color="red"
            disabled={Object.keys(formState.errors).length > 0}
            loading={loading}
          >
            {t('common:button.login')}
          </Button>
        </form>

        <Button
          className="flex items-center text-sm"
          onClick={events.onClickPasswordReset}
          size="medium"
          color="transparent"
          textColor="blue"
        >
          {t('common:button.forgot_password')}
        </Button>
        {/* create account button */}
        {hasSignupBtn && (
          <Button
            to={
              !setIsOpenSignUp
                ? `/account/signup?${new URLSearchParams({
                    redirect: redirectPath || '',
                  }).toString()}`
                : undefined
            }
            onClick={() => {
              if (setIsOpenSignUp) setIsOpenSignUp();
            }}
            color="transparent"
            textColor="red"
            border
            borderColor="red"
            className="w-full font-bold"
            size="medium"
          >
            {t('common:button.create_account')}
          </Button>
        )}
        {SocialProviderSequence.filter((provider) => provider).length > 0 && (
          <div
            className={classNames('mt-6 w-full', {
              'mx-8': !showInModal,
              'mx-4 sm:mx-8': showInModal,
            })}
          >
            <div
              className={classNames(
                'flex flex-col gap-4 items-center pt-4 pb-8 -mb-8 bg-teal-50 sm:rounded-b-2xl',
                {
                  'px-8 -mx-8': !showInModal,
                  'px-4 sm:px-8 -mx-4 sm:-mx-8 rounded-b-2xl': showInModal,
                }
              )}
            >
              <p className="text-base text-teal-600">
                <b>{t('component:login_card.description.login_with_provider')}</b>
              </p>
              <div className="flex w-full justify-center gap-4">
                {SocialProviderSequence.map(
                  (provider) =>
                    provider && (
                      <Button
                        key={provider}
                        className="flex size-14 items-center !rounded-lg p-3"
                        color="white"
                        onClick={() => events.onClickProviderLogin(provider)}
                      >
                        <Image
                          src={`/social/${provider.toLowerCase()}.svg`}
                          alt={`${provider} Icon`}
                          width={32}
                          height={32}
                          className="cursor-pointer"
                        />
                      </Button>
                    )
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {showModal && (
        <AppleLoginConfirmationModal
          type="login"
          onContinue={() => events.onClickProviderLogin(CognitoAuthProvider.APPLE)}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default LoginCard;
